// Die Karten „Dachpartie“ und „Bodenpartie“ in der Akte (E3c, S4 – PLAN.md,
// „E3c“, S4; ANFORDERUNGEN.md H31, H32):
//
//  - Dachpartie (extern): die Gegebenheiten am Dach (Höhe, Zugang, Hinweis –
//    sonst „nicht erfasst“), die Partie zum Antippen, Tag (Vorschlag: der
//    Montagetermin) und Uhrzeit; darunter „Noch von Hand“ und drei Schritte:
//    die Dateien herunterladen – die Dach-Unterlage als PDF
//    (dach-unterlage-pdf.ts), dazu K2-Liste und K2-Plan, sobald sie da sind –,
//    die Mail als Vorschau und „Mail an … öffnen“ (mailto:), dann „Beauftragt“.
//  - Bodenpartie (die eigenen Leute): die Partie, der Tag; darunter zwei
//    Schritte wie bei der Dachpartie (seit 3.11.2026): „Unterlage drucken“
//    (die bisherige Unterlage – vorerst druckt sie das Büro, Gernot 2.11.;
//    keine Mail), dann „Eingeteilt“.
//
// Je Karte „schon erledigt“. Was abgehakt ist, steht mit wann und wer da;
// „ändern“ öffnet es wieder (eine andere Partie, ein anderer Tag – der
// Schritt wird wieder offen, Befund 7).

import { useState } from "react";
import {
  dachFaktenZeile,
  dachHoeheText,
  DACH_ZUGANG_NAMEN,
  schrittIn,
  type AblaufEinsatz,
  type AblaufEinstellungen,
  type Akte,
  type CrmAufnahme,
  type EinsatzArt,
  type PartnerZeile,
} from "@happy-energy/kern";
import { dateiHolen, holen, Ikon, toastZeigen } from "@happy-energy/oberflaeche";
import { dateiAdresse, kalendertagZeigen, tagZeigen } from "../anzeige.ts";
import { PDF_IM_BROWSER, pdfHerunterladen } from "../unterlage/pdf-browser.ts";
import { pdfFertigMeldung } from "../unterlage/unterlage.ts";
import { useCrm } from "../zusammenhang.tsx";
import { dachMail, dachUnterlageName, mailtoLink, nurAnsprechpartner, tagKurz, uhrzeitText } from "./ablauf.ts";
import { K2Dateien, rahmenDerAkte } from "./BestellungKarte.tsx";
import { AnWen, MailSchritt, Meldung, NochVonHand, PartnerWahl, SchonErledigtKnopf, TagFeld, TextKopieren, unerwartetMelden, useSchicken } from "./gemeinsam.tsx";
import { KartenKopf, StandSatz } from "./kopf.tsx";

/** Der jüngste geltende Einsatz einer Art – an ihm ändert die Karte. */
function einsatzVon(akte: Akte, art: EinsatzArt): AblaufEinsatz | null {
  return akte.ablauf.einsaetze.filter((e) => e.art === art).at(-1) ?? null;
}

/** „Mo., 7. Dez. 2026, 7:30 Uhr“ */
function wannText(tag: string | null, uhrzeit: string | null): string {
  if (!tag) return "noch kein Tag";
  return uhrzeit ? `${kalendertagZeigen(tag)}, ${uhrzeitText(uhrzeit)}` : kalendertagZeigen(tag);
}

/** Partie, Tag und (bei der Dachpartie) Uhrzeit – ändert sich etwas und gibt es schon eine Partie, geht es gleich an den Server. */
function PartieFelder({
  akte,
  art,
  e,
  partien,
  mitUhrzeit,
  schicken,
  sendet,
  fertig,
}: {
  akte: Akte;
  art: EinsatzArt;
  e: AblaufEinsatz | null;
  partien: readonly PartnerZeile[];
  mitUhrzeit: boolean;
  schicken: ReturnType<typeof useSchicken>["schicken"];
  sendet: boolean;
  fertig: () => void;
}) {
  const termin = akte.anlage.montagetermin.am;
  // Ohne Partie merkt sich die Karte Tag und Uhrzeit, bis eine angetippt wird. Die gewählte Partie gilt sofort
  // (nicht erst, wenn die Akte neu geladen ist) – sonst ginge eine gleich danach getippte Uhrzeit verloren.
  const [tag, setTag] = useState<string>(e?.tag ?? termin ?? "");
  const [uhrzeit, setUhrzeit] = useState<string>(e?.uhrzeit ?? "");
  const [gewaehlt, setGewaehlt] = useState<string | null>(e?.partnerId ?? null);
  const [stand, setStand] = useState(e);
  if (stand?.id !== e?.id || stand?.tag !== e?.tag || stand?.uhrzeit !== e?.uhrzeit || stand?.partnerId !== e?.partnerId) {
    // Nur übernehmen, was sich am Server geändert hat – was gerade getippt wird, bleibt.
    if ((stand?.tag ?? null) !== (e?.tag ?? null)) setTag(e?.tag ?? termin ?? "");
    if ((stand?.uhrzeit ?? null) !== (e?.uhrzeit ?? null)) setUhrzeit(e?.uhrzeit ?? "");
    if (e?.partnerId) setGewaehlt(e.partnerId);
    setStand(e);
  }
  const name = art === "dach" ? "Dachpartie" : "Bodenpartie";
  const partnerId = e?.partnerId ?? gewaehlt;

  const setzen = (id: string, neuTag: string, neuUhrzeit: string) =>
    schicken("einsatz", { art, partnerId: id, tag: neuTag || null, uhrzeit: mitUhrzeit ? neuUhrzeit || null : null }).then((ok) => {
      if (ok) fertig();
    });

  return (
    <>
      <PartnerWahl
        name={name}
        partner={partien}
        gewaehlt={partnerId}
        gesperrt={sendet}
        waehlen={(id) => {
          setGewaehlt(id);
          void setzen(id, tag, uhrzeit);
        }}
      />
      <div className="ablauf-felder">
        <TagFeld
          name="Tag"
          wert={tag}
          gesperrt={sendet}
          speichern={(neu) => {
            setTag(neu);
            if (partnerId) void setzen(partnerId, neu, uhrzeit);
          }}
        />
        {mitUhrzeit && (
          <label className="crm-feld">
            Uhrzeit
            <input
              className="crm-eingabe"
              type="time"
              value={uhrzeit}
              disabled={sendet}
              onChange={(x) => setUhrzeit(x.target.value)}
              onBlur={(x) => {
                if (partnerId && x.target.value !== (e?.uhrzeit ?? "")) void setzen(partnerId, tag, x.target.value);
              }}
            />
          </label>
        )}
        <span className="leise klein ablauf-tag">{tag ? (tag === termin ? `${tagKurz(tag)} – der Montagetermin` : tagKurz(tag)) : "Vorschlag: der Montagetermin"}</span>
      </div>
    </>
  );
}

/** Erledigt: wer, wann, von wem – mit „ändern“. */
function Abgehakt({ e, partie, wort, aendern }: { e: AblaufEinsatz; partie: PartnerZeile | undefined; wort: string; aendern: (() => void) | null }) {
  return (
    <div className="ablauf-abgehakt">
      <p className="klein">
        {e.weg === "schon-erledigt"
          ? `als schon erledigt vermerkt (ohne System)${partie ? ` – ${partie.name}` : ""}`
          : `${partie?.name ?? "Partie"} · ${wannText(e.tag, e.uhrzeit)} – ${wort} ${tagZeigen(e.beauftragtAm!)}${e.beauftragtVon ? ` (${e.beauftragtVon.name})` : ""}`}
      </p>
      {aendern && (
        <button type="button" className="crm-knopf leise klein" onClick={aendern}>
          ändern
        </button>
      )}
    </div>
  );
}

// --- Dachpartie -------------------------------------------------------------------------------

/** „Dach-Unterlage herunterladen“: die Besichtigung holen, den K2-Plan dazu, das PDF bauen und sichern. */
function DachUnterlageKnopf({ akte, e, partie }: { akte: Akte; e: AblaufEinsatz | null; partie: PartnerZeile | undefined }) {
  const { ich } = useCrm();
  const [stand, setStand] = useState<{ fertig: number; gesamt: number } | null>(null);
  const aufnahmeId = akte.ablauf.unterlageAufnahmeId;

  async function bauen() {
    if (!aufnahmeId) return;
    setStand({ fertig: 0, gesamt: 0 });
    try {
      const a = await holen<CrmAufnahme>(`/api/crm/aufnahme/${aufnahmeId}`);
      const k2 = akte.anlage.k2.pruefsumme ? new Uint8Array(await (await dateiHolen(dateiAdresse(akte.anlage.k2.pruefsumme))).arrayBuffer()) : null;
      const { dachUnterlagePdfBauen } = await import("./dach-unterlage-pdf.ts");
      const titel = dachUnterlageName(akte.kunde.name, rahmenDerAkte(akte));
      const ergebnis = await dachUnterlagePdfBauen(
        {
          a,
          firma: ich.firma.name,
          titel,
          termin: { tag: e?.tag ?? akte.anlage.montagetermin.am, uhrzeit: e?.uhrzeit ?? null },
          partie: partie?.name ?? null,
          module: akte.ablauf.module,
          dach: akte.ablauf.dach,
          k2Pdf: k2,
        },
        PDF_IM_BROWSER,
        (fertig, gesamt) => setStand({ fertig, gesamt }),
      );
      pdfHerunterladen(ergebnis.pdf, `${titel}.pdf`);
      const meldung = pdfFertigMeldung(`${titel}.pdf`, ergebnis.fotosFehlen);
      toastZeigen(ergebnis.k2 === "nicht-einbettbar" ? "Dach-Unterlage fertig – den K2-Plan bitte als eigene Datei herunterladen und anhängen." : meldung.text, ergebnis.k2 === "nicht-einbettbar" ? "fehler" : meldung.art);
    } catch (f) {
      unerwartetMelden(f, "Dach-Unterlage");
      toastZeigen("Die Dach-Unterlage ließ sich nicht bauen – bitte noch einmal versuchen.", "fehler");
    } finally {
      setStand(null);
    }
  }

  if (!aufnahmeId) {
    return (
      <button type="button" className="crm-knopf rand klein" disabled title="Die Dach-Unterlage entsteht aus der freigegebenen Besichtigung">
        <Ikon name="herunterladen" groesse="k" /> Dach-Unterlage (PDF) herunterladen – nach dem Freigeben
      </button>
    );
  }
  return (
    <button type="button" className="crm-knopf rand klein" onClick={() => void bauen()} disabled={stand !== null}>
      {stand ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="herunterladen" groesse="k" />}
      {stand ? `Dach-Unterlage wird gebaut … ${stand.fertig} von ${stand.gesamt}` : "Dach-Unterlage (PDF) herunterladen"}
    </button>
  );
}

export function DachpartieKarte({ akte, partner, einstellungen, neuLaden }: { akte: Akte; partner: readonly PartnerZeile[]; einstellungen: AblaufEinstellungen; neuLaden: () => void }) {
  const { ich } = useCrm();
  const { sendet, meldung, schicken } = useSchicken(akte.anlage.id, "Dachpartie", neuLaden);
  const [aendern, setAendern] = useState(false);
  const s = schrittIn(akte.kette, "dachpartie");
  const freigegeben = ["erledigt", "vor-dem-update"].includes(schrittIn(akte.kette, "freigeben").stand);
  const partien = partner.filter((p) => p.art === "dachpartie");
  const e = einsatzVon(akte, "dach");
  const partie = e?.partnerId ? partner.find((p) => p.id === e.partnerId) : undefined;
  const erledigt = e !== null && e.beauftragtAm !== null;
  const d = akte.ablauf.dach;

  // Der angetippte Ansprechpartner gilt für diese Partie – eine andere Partie beginnt wieder beim ersten.
  const [wahl, setWahl] = useState<{ partieId: string; nr: number } | null>(null);
  const nr = wahl !== null && wahl.partieId === partie?.id ? wahl.nr : 0;
  const mail = partie && e ? dachMail(e, nurAnsprechpartner(partie, nr), { vorlage: einstellungen.mailvorlagen.dachpartie, objekt: akte.objekt, firma: ich.firma.name, module: akte.ablauf.module, kunde: akte.kunde.name }) : null;
  const link = mail ? mailtoLink(mail.an, mail.betreff, mail.text) : null;
  const gehtNicht = s.stand === "geht-noch-nicht" ? `Beauftragen ${s.grund ?? "geht noch nicht"}.` : null;
  const blockiert = gehtNicht ?? (mail && mail.fehlt.length > 0 ? `Es fehlt noch: ${mail.fehlt.join(", ")}.` : null);

  return (
    <>
      <KartenKopf ikon="dach" titel="Dachpartie" schritt={s} />
      <StandSatz schritt={s} />

      <div className="ablauf-dach" aria-label="Gegebenheiten am Dach">
        <span className="leise klein">Am Dach:</span>{" "}
        <b className="klein">{d ? dachFaktenZeile(d) : "Höhe und Zugang nicht erfasst"}</b>
        {d?.hinweis && <p className="klein">„{d.hinweis}“</p>}
        {/* Seit S5 trägt der Verkäufer sie in der HE-Doku ein – fehlen sie, kann er sie mit „Etwas nachreichen“ nachtragen. */}
        {!d && (
          <p className="leise klein">
            Trägt der Verkäufer in der HE-Doku an der Station Dach ein – auch später mit „Etwas nachreichen“. Bis dahin steht auf der Dach-Unterlage
            „nicht erfasst“.
          </p>
        )}
      </div>

      {erledigt && !aendern ? (
        <Abgehakt e={e} partie={partie} wort="beauftragt" aendern={e.weg === "mail" ? () => setAendern(true) : null} />
      ) : (
        <>
          {aendern && <p className="akte-geaendert klein">Schon beauftragt – eine andere Partie, ein anderer Tag oder eine andere Uhrzeit öffnet den Schritt wieder (Mail erneut).</p>}
          <PartieFelder akte={akte} art="dach" e={e} partien={partien} mitUhrzeit schicken={schicken} sendet={sendet} fertig={() => setAendern(false)} />
          {aendern && (
            <button type="button" className="crm-knopf rand klein" onClick={() => setAendern(false)}>
              Fertig
            </button>
          )}
        </>
      )}

      {/* Wie die Bestellung seit Gernots Augentest E3c (3.11.2026): „Noch von Hand“, dann der Reihe nach – Unterlage, Mail, abhaken. */}
      {mail && link && (e?.beauftragtAm === null || s.hinweis !== null) && (
        <section className="ablauf-mail" aria-label={`Mail an ${partie!.name}`}>
          <NochVonHand />
          <ol className="ablauf-schritte">
            <MailSchritt nr={1} titel="Die Dateien herunterladen">
              <div className="ablauf-dateien">
                <DachUnterlageKnopf akte={akte} e={e} partie={partie} />
                <p className="leise klein">
                  Auf der Dach-Unterlage: Name und Adresse, Termin, Module, {d?.hoehe_m !== undefined ? `Höhe ${dachHoeheText(d.hoehe_m)}` : "Höhe"}
                  {d?.zugang ? ` (${DACH_ZUGANG_NAMEN[d.zugang]})` : ""}, die Dachfotos mit Zeichnung und der K2-Plan – keine Notizen, kein Telefon, nichts aus dem Haus.
                </p>
                {/* Seit 3.11.2026 abends auch hier die Dateien aus K2 – was die Partie braucht, hängt an, wer die Mail schickt. */}
                <K2Dateien akte={akte} fehltSagen={false} neuLaden={neuLaden} />
              </div>
            </MailSchritt>
            <MailSchritt nr={2} titel="Die Mail öffnen, die Dateien anhängen, abschicken">
              <AnWen p={partie!} an={mail.an} nr={nr} waehlen={(n) => setWahl({ partieId: partie!.id, nr: n })} />
              <div className="ablauf-vorschau" role="group" aria-label={`Mail an ${partie!.name} – Vorschau`}>
                <p className="klein">
                  <span className="leise">Betreff:</span> {mail.betreff}
                </p>
                <pre>{mail.text}</pre>
              </div>
              {blockiert && <p className="akte-geaendert klein">{blockiert}</p>}
              <div className="crm-knopf-reihe">
                {mail.an.length > 0 && !blockiert && !sendet ? (
                  <a className="crm-knopf dunkel" href={link.href}>
                    <Ikon name="senden" groesse="k" /> Mail an {partie!.name} öffnen
                  </a>
                ) : (
                  <button type="button" className="crm-knopf dunkel" disabled title={blockiert ?? "Keine Mail-Adresse – bitte den Text kopieren"}>
                    <Ikon name="senden" groesse="k" /> Mail an {partie!.name} öffnen
                  </button>
                )}
                {(mail.an.length === 0 || !link.mitText) && <TextKopieren text={mail.text} wo="Dachpartie" />}
              </div>
            </MailSchritt>
            {e?.beauftragtAm === null && (
              <MailSchritt nr={3} titel="Abhaken">
                <div className="crm-knopf-reihe">
                  <button type="button" className="crm-knopf lime" disabled={sendet || blockiert !== null} onClick={() => void schicken("beauftragt", { art: "dach", einsatzId: e.id }, `${partie!.name} beauftragt`)}>
                    <Ikon name="haken" groesse="k" /> Beauftragt
                  </button>
                  <span className="leise klein">Erst, wenn die Mail an {partie!.name} abgeschickt ist.</span>
                </div>
              </MailSchritt>
            )}
          </ol>
        </section>
      )}
      <Meldung text={meldung} />
      {!erledigt && freigegeben && (
        <SchonErledigtKnopf frage="Die Dachpartie schon ohne System beauftragt?" sendet={sendet} los={() => void schicken("einsatz/schon-erledigt", { art: "dach" }, "Dachpartie: als schon erledigt vermerkt")} />
      )}
    </>
  );
}

// --- Bodenpartie -----------------------------------------------------------------------------

/** „Unterlage drucken“ – die Druckansicht der freigegebenen Besichtigung; vorher gesperrt. */
function UnterlageDrucken({ aufnahmeId }: { aufnahmeId: string | null }) {
  return aufnahmeId ? (
    <a className="crm-knopf dunkel klein" href={`#/unterlage/${aufnahmeId}`}>
      <Ikon name="drucker" groesse="k" /> Unterlage drucken
    </a>
  ) : (
    <button type="button" className="crm-knopf dunkel klein" disabled title="Die Unterlage entsteht mit der Freigabe">
      <Ikon name="drucker" groesse="k" /> Unterlage drucken
    </button>
  );
}

export function BodenpartieKarte({ akte, partner, neuLaden }: { akte: Akte; partner: readonly PartnerZeile[]; neuLaden: () => void }) {
  const { sendet, meldung, schicken } = useSchicken(akte.anlage.id, "Bodenpartie", neuLaden);
  const [aendern, setAendern] = useState(false);
  const s = schrittIn(akte.kette, "bodenpartie");
  const freigegeben = ["erledigt", "vor-dem-update"].includes(schrittIn(akte.kette, "freigeben").stand);
  const partien = partner.filter((p) => p.art === "bodenpartie");
  const e = einsatzVon(akte, "boden");
  const partie = e?.partnerId ? partner.find((p) => p.id === e.partnerId) : undefined;
  const erledigt = e !== null && e.beauftragtAm !== null;
  const aufnahmeId = akte.ablauf.unterlageAufnahmeId;
  const blockiert = s.stand === "geht-noch-nicht" ? `Einteilen ${s.grund ?? "geht noch nicht"}.` : e && e.tag === null ? "Es fehlt noch der Tag." : null;

  return (
    <>
      <KartenKopf ikon="werkzeug" titel="Bodenpartie" schritt={s} />
      <StandSatz schritt={s} />

      {erledigt && !aendern ? (
        <Abgehakt e={e} partie={partie} wort="eingeteilt" aendern={e.weg === "druck" ? () => setAendern(true) : null} />
      ) : (
        <>
          {aendern && <p className="akte-geaendert klein">Schon eingeteilt – eine andere Partie oder ein anderer Tag öffnet den Schritt wieder (Unterlage erneut drucken).</p>}
          <PartieFelder akte={akte} art="boden" e={e} partien={partien} mitUhrzeit={false} schicken={schicken} sendet={sendet} fertig={() => setAendern(false)} />
          {aendern && (
            <button type="button" className="crm-knopf rand klein" onClick={() => setAendern(false)}>
              Fertig
            </button>
          )}
        </>
      )}

      {/* Eingeteilt: nur noch der Knopf zum (erneuten) Drucken. Sonst wie Bestellung und Dachpartie der Reihe nach (Gernots Augentest E3c, 3.11.2026). */}
      {erledigt ? (
        <div className="crm-knopf-reihe">
          <UnterlageDrucken aufnahmeId={aufnahmeId} />
        </div>
      ) : (
        <section className="ablauf-mail" aria-label="Unterlage für die Bodenpartie">
          <ol className="ablauf-schritte">
            <MailSchritt nr={1} titel="Die Unterlage drucken">
              <div className="crm-knopf-reihe">
                <UnterlageDrucken aufnahmeId={aufnahmeId} />
                <span className="leise klein">Für die Montage – vorerst gedruckt, bis die Partien eine eigene App haben.</span>
              </div>
            </MailSchritt>
            <MailSchritt nr={2} titel="Abhaken">
              {blockiert && e?.partnerId && <p className="akte-geaendert klein">{blockiert}</p>}
              <div className="crm-knopf-reihe">
                <button
                  type="button"
                  className="crm-knopf lime"
                  disabled={sendet || blockiert !== null || !e?.partnerId}
                  onClick={() => {
                    if (e) void schicken("beauftragt", { art: "boden", einsatzId: e.id }, `${partie?.name ?? "Bodenpartie"} eingeteilt`);
                  }}
                >
                  <Ikon name="haken" groesse="k" /> Eingeteilt
                </button>
                <span className="leise klein">{e?.partnerId ? `Erst, wenn die Unterlage gedruckt und bei ${partie?.name ?? "der Partie"} ist.` : "Zuerst oben die Partie antippen."}</span>
              </div>
            </MailSchritt>
          </ol>
        </section>
      )}
      <Meldung text={meldung} />
      {!erledigt && freigegeben && (
        <SchonErledigtKnopf frage="Die Bodenpartie schon ohne System eingeteilt?" sendet={sendet} los={() => void schicken("einsatz/schon-erledigt", { art: "boden" }, "Bodenpartie: als schon erledigt vermerkt")} />
      )}
    </>
  );
}
